import React, { useState, useCallback, useEffect } from 'react';
import {
  IconButton,
  Grid,
  Box,
  Typography,
  Drawer,
  Paper,
  Stack,
  Toolbar,
  Button,
  Backdrop,
  CircularProgress,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { useUser } from '@/providers';
import { ArrowBack } from '@mui/icons-material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import CheckpointField from './CheckpointField';
import { fetchCheckpoints } from '../../../actions/checkpoints';
import { fetchPublicCheckpoints } from '../../../actions/publicCheckpoints';

export default function CheckpointDrawer({
  open,
  handleClose,
  initialValues,
  setValue,
}) {
  const dispatch = useDispatch();
  const user = useUser();
  const checkpoints = useSelector((state) => state.checkpoints);
  const publicCheckpoints = useSelector((state) => state.publicCheckpoints);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const safe_initial_values = Array.isArray(initialValues) ? initialValues : [];

  const [selectedCheckpoints, setSelectedCheckpoints] = useState(
    safe_initial_values.map((checkpoint) => ({ ...checkpoint }))
  );

  const [checkpoint_errors, setCheckpointErrors] = useState(
    new Array(safe_initial_values.length).fill(false)
  );

  useEffect(() => {
    const safe = Array.isArray(initialValues) ? initialValues : [];
    setSelectedCheckpoints(safe.map((checkpoint) => ({ ...checkpoint })));
  }, [initialValues]);

  useEffect(() => {
    (async () => {
      if (checkpoints === null) {
        if (user?.selected_project) {
          dispatch(fetchCheckpoints(user.selected_project));
        }
        return;
      }
    })();
  }, [dispatch, checkpoints, user?.selected_project]);

  useEffect(() => {
    (async () => {
      if (publicCheckpoints === null) {
        dispatch(fetchPublicCheckpoints());
        return;
      }
    })();
  }, [dispatch, publicCheckpoints]);

  useEffect(() => {
    setLoading(checkpoints === null || publicCheckpoints === null);
  }, [checkpoints, publicCheckpoints]);

  const calculateError = useCallback(
    (checkpoint_errors) => {
      const errors_set = new Set([...checkpoint_errors]);
      if (
        errors_set.size > 1 ||
        ([...errors_set][0] && [...errors_set][0] !== false)
      ) {
        setError(true);
      } else {
        setError(false);
      }
    },
    [setError]
  );

  const setCheckpointError = useCallback(
    (i, error) => {
      const new_errors = [...checkpoint_errors];
      new_errors[i] = error;

      setCheckpointErrors(new_errors);
      calculateError(new_errors);
    },
    [checkpoint_errors, setCheckpointErrors, calculateError]
  );

  const addCheckpoint = useCallback(() => {
    const new_checkpoints = [
      ...selectedCheckpoints,
      { id: '', as: '', public: false },
    ];
    setSelectedCheckpoints(new_checkpoints);
    const new_errors = [...checkpoint_errors, 'Checkpoint cannot be blank'];
    setCheckpointErrors(new_errors);
    calculateError(new_errors);
  }, [
    selectedCheckpoints,
    checkpoint_errors,
    setSelectedCheckpoints,
    calculateError,
    setCheckpointErrors,
  ]);

  const removeCheckpoint = useCallback(
    (i) => {
      const new_checkpoints = [...selectedCheckpoints];
      new_checkpoints.splice(i, 1);

      setSelectedCheckpoints(new_checkpoints);
      const new_errors = [...checkpoint_errors];
      new_errors.splice(i, 1);
      setCheckpointErrors(new_errors);
      calculateError(new_errors);
    },
    [
      selectedCheckpoints,
      checkpoint_errors,
      setSelectedCheckpoints,
      setCheckpointErrors,
      calculateError,
    ]
  );

  const setCheckpointValue = useCallback(
    (i, values) => {
      const new_checkpoints = [...selectedCheckpoints];
      new_checkpoints[i] = values;
      setSelectedCheckpoints(new_checkpoints);
    },
    [selectedCheckpoints, setSelectedCheckpoints]
  );

  const submit = useCallback(() => {
    // Enrich checkpoints with name and size from Redux store
    const allCheckpoints = [...(checkpoints || []), ...(publicCheckpoints || [])];
    const enrichedCheckpoints = selectedCheckpoints.map((checkpoint) => {
      if (!checkpoint.id) {
        return checkpoint;
      }
      const fullCheckpoint = allCheckpoints.find(
        (cp) => cp.checkpoint_uuid === checkpoint.id
      );
      return {
        ...checkpoint,
        name: fullCheckpoint ? fullCheckpoint.name : checkpoint.as || '',
        size: fullCheckpoint ? fullCheckpoint.size || fullCheckpoint.used_size : undefined,
      };
    });
    setValue(enrichedCheckpoints);
    handleClose();
  }, [selectedCheckpoints, setValue, handleClose, checkpoints, publicCheckpoints]);

  return (
    <Drawer
      sx={{ height: '100%' }}
      anchor='right'
      open={open}
      onClose={handleClose}
    >
      <Grid
        container
        spacing={2}
        direction='row'
        sx={{
          display: 'flex',
          width: '100%',
          height: '100%',
          margin: '0px',
          padding: '0px 0px 0px 0px !important',
          '.MuiGrid-root': {
            padding: '0px 0px 0px 0px',
          },
          maxWidth: '550px',
        }}
      >
        <Grid
          container
          justifyContent='flex-start'
          sx={{
            maxHeight: '75px',
          }}
        >
          <Paper
            sx={{
              display: 'flex',
              height: '100%',
              width: '100%',
            }}
            elevation={1}
          >
            <Toolbar>
              <Stack spacing={2} direction='row' alignItems='center'>
                <IconButton onClick={() => handleClose()}>
                  <ArrowBack />
                </IconButton>
                <Typography variant='h5'>Manage Checkpoints</Typography>
              </Stack>
            </Toolbar>
          </Paper>
        </Grid>
        <Grid
          size={{ xs: 12, md: 12, lg: 12 }}
          sx={{
            display: 'flex',
            height: '100%',
            width: '100%',
          }}
        >
          <Backdrop open={loading}>
            <CircularProgress />
          </Backdrop>
          <Box sx={{ padding: '16px' }}>
            <Stack spacing={2} direction='column' alignItems='center'>
              {selectedCheckpoints.map((value, index) => {
                return (
                  <CheckpointField
                    key={`checkpoint-${index}`}
                    index={index}
                    value={value}
                    error={checkpoint_errors[index]}
                    setError={setCheckpointError}
                    setValue={setCheckpointValue}
                    remove={removeCheckpoint}
                  />
                );
              })}
              <Button
                variant='outlined'
                color='brand'
                onClick={() => addCheckpoint()}
              >
                <Stack direction='row' spacing={1} alignItems='center'>
                  <AddCircleOutlineIcon />
                  <Typography variant='button' display='block' gutterBottom>
                    Add Checkpoint
                  </Typography>
                </Stack>
              </Button>
            </Stack>
          </Box>
        </Grid>
        <Grid
          container
          sx={{
            maxHeight: '50px',
            position: 'absolute',
            bottom: '0px',
            width: '100%',
          }}
        >
          <Paper sx={{ display: 'flex', width: '100%' }} elevation={1}>
            <Toolbar>
              <Stack spacing={2} direction='row'>
                <Button
                  variant='contained'
                  color='brand'
                  onClick={submit}
                  disabled={error}
                >
                  Save
                </Button>
                <Button
                  variant='outlined'
                  onClick={() => {
                    const safe = Array.isArray(initialValues) ? initialValues : [];
                    setSelectedCheckpoints(safe.map((checkpoint) => ({ ...checkpoint })));
                    setCheckpointErrors(new Array(safe.length).fill(false));
                    setError(false);
                    handleClose();
                  }}
                >
                  Cancel
                </Button>
              </Stack>
            </Toolbar>
          </Paper>
        </Grid>
      </Grid>
    </Drawer>
  );
}
